import env from '@beam-australia/react-env'
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { TextFieldLabel } from 'src/components/UI'
import Table from 'src/components/UI/CustomUI/organisms/Table'
import { AlignType, HeaderType } from 'src/components/UI/CustomUI/organisms/Table/types'
import inspectionReportsActions from 'src/ducks/inspectionReports/actions'
import { getInspectionReports } from 'src/ducks/inspectionReports/selectors'
import { dateFormatString, ROWS_PER_PAGE_EXTRA_EXTENDED } from 'src/helpers'
import styles from './styles.module.scss'

type Props = {}

const headers: HeaderType[] = [
    {
        id: 'inspectionId',
        label: 'Inspection ID',
        custom: true,
        align: 'left' as AlignType,
        padding: "0px 8px",
        minWidth: 150,
        highlightRows: true,
        Element: (row: any): JSX.Element => {
            return (
                <Box style={{ padding: '1px 10px' }}>
                    <a target='_blank' rel='noreferrer' style={{ cursor: 'pointer' }} href={`/inspection-reports/${row.id}`}>
                        <Typography className={styles.Companies__companyName} style={{ color: 'var(--blue700)' }}>
                            {`${row.id || "-"}`}
                        </Typography>
                    </a>
                </Box>
            )
        },
    },
    {
        id: 'estimateId',
        label: 'Estimate ID',
        custom: true,
        align: 'left' as AlignType,
        minWidth: 150,
        padding: "0px 8px",
        highlightRows: true,
        Element: (row: any): JSX.Element => {
            return (
                <Box style={{ padding: '1px 10px' }}>
                    <a target='_blank' rel='noreferrer' style={{ cursor: 'pointer' }} href={`/estimates/${row.estimate_id}`}>
                        <Typography className={styles.Companies__companyName} style={{ color: 'var(--blue700)' }}>
                            {row.estimate_id || "-"}
                        </Typography>
                    </a>
                    {/* <Typography className={styles.Companies__companyName}>
              {row.orderPublicId || "-"}
            </Typography> */}
                </Box>
            )
        },
    },
    {
        id: 'status',
        label: 'Status',
        custom: true,
        align: 'left' as AlignType,
        minWidth: 150,
        padding: "0px 8px",
        highlightRows: true,
        Element: (row: any): JSX.Element => {
            const statusColor = row.status === 'COMPLETED' ? '#3EAF3F' : row.status === 'PROCESSING' || row.status === 'PROGRESS' ? '#E1A325' : '#E14942'
            return <Typography style={{ color: statusColor, fontWeight: 700 }}>{row.status || "-"}</Typography>
        },
    },
    {
        id: 'startedOn',
        label: 'Started On',
        custom: true,
        align: 'left' as AlignType,
        minWidth: 150,
        Element: (row: any): JSX.Element => {
            return <Typography>{dateFormatString(row.started_on, 'MM/DD/YYYY hh:mm a') || "-"}</Typography>
        },
    },
    {
        id: 'completedOn',
        label: 'Completed On',
        custom: true,
        align: 'left' as AlignType,
        minWidth: 150,
        Element: (row: any): JSX.Element => {
            return <Typography>{dateFormatString(row.completed_on, 'MM/DD/YYYY hh:mm a') || "-"}</Typography>
        },
    },
    {
        id: 'processingTime',
        label: 'Processing Time',
        custom: true,
        align: 'left' as AlignType,
        minWidth: 150,
        Element: (row: any): JSX.Element => {
            const dateStarted = new Date(row.started_on)
            const dateCompleted = new Date(row.completed_on)
            const processingTime = dateCompleted.getTime() - dateStarted.getTime()
            const processingTimeInMinutes = processingTime / (1000 * 60)
            const isCompleted = row.status === 'COMPLETED'
            return <Typography>{isCompleted ? `${processingTimeInMinutes.toFixed(2)} minutes` : "-"}</Typography>
        },
    },
    {
        id: 'documentUrl',
        label: 'Document URL',
        custom: true,
        align: 'left' as AlignType,
        minWidth: 150,
        Element: (row: any): JSX.Element => {
            return <a target='_blank' rel='noreferrer' style={{ cursor: 'pointer' }} href={row.document_url}>
                <Typography style={{ color: 'var(--blue700)' }}>Document</Typography>
            </a>
        },
    },
]

const InspectionReports = (props: Props) => {
    const [loadingReports, setLoadingReports] = useState(false)
    const [estimateId, setEstimateId] = useState('')
    const [documentUrl, setDocumentUrl] = useState('')
    const [isCreating, setIsCreating] = useState(false)
    const dispatch = useDispatch()

    const inspectionReports = useSelector(getInspectionReports)

    const fetchInspectionReports = (page: number = 1, limit: number = 100) => {
        setLoadingReports(true)
        dispatch(inspectionReportsActions.fetchInspectionReports({ limit, page, start: 0 }, () => {
            setLoadingReports(false)
        }))
    }

    const handleCreateInspectionReport = () => {
        if (!estimateId.trim() || !documentUrl.trim()) {
            alert('Please complete all fields')
            return
        }

        setIsCreating(true)

        dispatch(inspectionReportsActions.createInspectionReport({
            estimateId: estimateId.trim(),
            fileUrl: documentUrl.trim()
        }, (success) => {
            if (success) {
                fetchInspectionReports()
            }
            setIsCreating(false)
            setEstimateId('')
            setDocumentUrl('')
        }))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchInspectionReports()
    }, [])

    const isProduction = env('ENVIRONMENT') === 'prod'

    return (
        <div>
            {!isProduction && <Box sx={{ mb: 3 }}>
                {/* Formulario de creación */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                            Create New Inspection Report
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <TextFieldLabel
                                    fullWidth
                                    label="Estimate ID"
                                    value={estimateId}
                                    onChange={(e) => setEstimateId(e.target.value)}
                                    placeholder="Enter the Estimate ID"
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextFieldLabel
                                    fullWidth
                                    label="Document URL"
                                    value={documentUrl}
                                    onChange={(e) => setDocumentUrl(e.target.value)}
                                    placeholder="Enter the document URL"
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleCreateInspectionReport}
                                    disabled={isCreating || !estimateId.trim() || !documentUrl.trim()}
                                    sx={{
                                        minWidth: 120,
                                        height: 40
                                    }}
                                >
                                    {isCreating ? 'Creating...' : 'Create'}
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>}
            <div>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2', mb: 3 }}>
                    AI Inspection Conversion
                </Typography>
                <Table
                    headers={headers}
                    loading={loadingReports}
                    hasPagination
                    sortable
                    defaultOrder="desc"
                    rowsPerPageOptions={ROWS_PER_PAGE_EXTRA_EXTENDED}
                    rowsPerPageDefault={ROWS_PER_PAGE_EXTRA_EXTENDED[0]}
                    callToApi={(start, limit) => {
                        fetchInspectionReports(start + 1, limit)
                    }}
                    totalItems={inspectionReports?.pagination.total || 0}
                >
                    {inspectionReports?.data}
                </Table>
            </div>
        </div>
    )
}

export default InspectionReports